import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CommonImage from '@components/CommonImage';
import CustomReactSelect from '@components/CustomReactSelect';
import CustomTooltip, { POSITIONS } from '@components/CustomTooltip';
import {
	checkValidId,
	INITIAL_STATE,
	organizeResponse,
	POPUP_VARIENTS,
	UPDATE_ENTITY_TYPE,
	validatePopupRequiredFields
} from '@components/rangePlan/rangePlanUtils';

import useFormInput from '@hooks/useFormInput';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { getCategories } from '@store/design/action';

import { getFabricConstraction } from '@services/commonService';
import { fetchFitTypeTags } from '@services/rangePlanService';

import { printApiError } from '@utils/Util';

export const Popup = ({
	title,
	selectedOptions = [],
	inputs = [],
	onCancel,
	onSubmit,
	options = [],
	varient,
	onInputChange,
	onSelectOption,
	isShowBrandInfo = false,
	brandPriceDetails = {}
}) => {
	return (
		<>
			<div className="type-details-popup shadow-8dp p-3">
				{/* Display Select options if present */}
				{varient === POPUP_VARIENTS.DROP_DOWN && (
					<div className="select-component chip-component mb-3 c">
						<CustomReactSelect
							allItems={options}
							onSetSelectedItem={(value) => onSelectOption(value)}
							selectedValue={selectedOptions}
							title={'Select'}
							isMulti={true}
						/>
					</div>
				)}

				{/* Display inputs if present */}
				{varient === POPUP_VARIENTS.INPUT && (
					<div className="row">
						{isShowBrandInfo && (
							<div className="col-12">
								<div className="pb-3 d-flex justify-content-between">
									<p className="regular-14">Target production price:</p>
									<div className="ml-1 cursor-pointer">
										<CustomTooltip
											text={
												'Max ($): Initially, the system has reduced 10% from the maximum buying price.'
											}
											position={POSITIONS.TOP}
											arrowCenter={true}
											className="custom-tooltip"
										>
											<CommonImage src="/icons/info.svg" alt="info" />
										</CustomTooltip>
									</div>
								</div>
							</div>
						)}
						{inputs.map((input, index) => (
							<div className={`col-6 ${index === 0 ? 'pr-1' : 'pl-1'}`} key={index}>
								<div className="form-group">
									<label htmlFor={`input-${title}-${index}`}>{input.label}</label>
									<input
										type={input.type || 'text'}
										id={`input-${title}-${index}`}
										autoFocus={input.name === 'min'}
										value={input?.value}
										onChange={onInputChange}
										name={input?.name}
									/>
									{input?.error && <p className="error">{input?.error}</p>}
								</div>
							</div>
						))}
						{isShowBrandInfo && (
							<div className="ml-3 mb-3">
								<p>
									{brandPriceDetails?.brandName}{' '}
									{brandPriceDetails?.brandName && 'buying price:'} <br />
									{brandPriceDetails?.brandMinPrice &&
										`$${brandPriceDetails?.brandMinPrice}`}
									{brandPriceDetails?.brandMaxPrice && ' - '}
									{brandPriceDetails?.brandMaxPrice &&
										`$${brandPriceDetails?.brandMaxPrice}`}
								</p>
							</div>
						)}
					</div>
				)}

				{/* Footer buttons */}
				<div className="footer-buttons text-right">
					<button
						className="button text size32 p-0 mr-1"
						onClick={(e) => {
							e.stopPropagation();
							onCancel();
						}}
					>
						CANCEL
					</button>
					<button className="button text size32 p-0" onClick={onSubmit}>
						SUBMIT
					</button>
				</div>
			</div>
		</>
	);
};

// Fabric Category Popup Component
export const FabricCategory = ({ setSelectedTargetCategoryId, onUpdateTarget, item, id, entityType }) => {
	const [fabricCategories, setFabricCategories] = useState([]);
	const [selectedFabricTypes, setSelectedFabricTypes] = useState([]);
	const dispatch = useDispatch();

	useEffect(() => {
		const fabricTypes = organizeResponse(item?.targetFabricConstructions);
		setSelectedFabricTypes(fabricTypes || []);
	}, [id, entityType]);

	const fetchFabricCategories = async () => {
		try {
			dispatch(openLoader());
			const response = await getFabricConstraction();
			const updatedResponse = organizeResponse(response);
			const updatedResponseWithConstant = updatedResponse.map((item) => ({
				...item,
				value: item.id,
				label: item.name
			}));
			setFabricCategories(updatedResponseWithConstant);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleSelectedOptions = (value) => {
		setSelectedFabricTypes(value);
	};

	const handleRemoveSelectedOptions = (value) => {
		setSelectedFabricTypes((prev) => prev.filter((type) => type.id !== value.id));
	};

	useEffect(() => {
		fetchFabricCategories();
	}, []);

	useEffect(() => {
		dispatch(getCategories());
	}, []);

	const handleSubmitCategories = () => {
		if (selectedFabricTypes.length === 0) {
			return;
		}
		const body = {
			id: checkValidId(item),
			entityId: id,
			categoryId: item?.categoryResponse?.id,
			fabricType: item?.fabricType?.constant,
			productGroupId: item?.rangePlanningId?.productGroupId,
			fieldName: UPDATE_ENTITY_TYPE.CONSTRUCTION,
			entityType: entityType,
			targetFabricConstructionIds: selectedFabricTypes.map((type) => type.id)
		};
		onUpdateTarget(body, () => {
			setSelectedFabricTypes([]);
			setSelectedTargetCategoryId(null);
		});
	};

	const handleCancel = () => {
		setSelectedFabricTypes([]);
		setSelectedTargetCategoryId(null);
	};

	return (
		<Popup
			onSelectOption={handleSelectedOptions}
			options={fabricCategories}
			varient={POPUP_VARIENTS.DROP_DOWN}
			title="Fabric Category"
			selectedOptions={selectedFabricTypes}
			onCancel={handleCancel}
			onSubmit={() => handleSubmitCategories()}
			handleRemoveSelectedOptions={handleRemoveSelectedOptions}
		/>
	);
};

// Fit Type Popup Component
export const FitTypePopup = ({ setSelectedTargetFitTypeId, onUpdateTarget, item, id, entityType }) => {
	const [fitTypes, setFitTypes] = useState([]);
	const [selectedFitTypes, setSelectedFitTypes] = useState([]);
	const dispatch = useDispatch();

	useEffect(() => {
		const fitTypes = organizeResponse(item?.targetFitTypes, 'text');
		setSelectedFitTypes(fitTypes);
	}, [id, entityType]);

	const fetchFittingResponse = async () => {
		try {
			dispatch(openLoader());
			const response = await fetchFitTypeTags('');
			const updatedResponse = organizeResponse(response, 'text');
			setFitTypes(updatedResponse);
			dispatch(closeLoader());
		} catch (e) {
			printApiError(e);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		fetchFittingResponse();
	}, []);

	const handleSelectPopup = (value) => {
		setSelectedFitTypes(value);
	};

	const handleRemoveSelectedOptions = (option) => {
		setSelectedFitTypes((prev) => prev.filter((type) => type.id !== option.id));
	};

	const handleSubmitFitType = () => {
		if (selectedFitTypes.length === 0) {
			return;
		}
		const body = {
			id: checkValidId(item),
			entityId: id,
			categoryId: item?.categoryResponse?.id,
			fabricType: item?.fabricType?.constant,
			productGroupId: item?.rangePlanningId?.productGroupId,
			fieldName: UPDATE_ENTITY_TYPE.FIT_TYPE,
			entityType: entityType,
			targetFitTypeIds: selectedFitTypes.map((type) => type.id)
		};
		onUpdateTarget(body, () => {
			setSelectedFitTypes([]);
			setSelectedTargetFitTypeId(null);
		});
	};

	const handleCancel = () => {
		setSelectedFitTypes([]);
		setSelectedTargetFitTypeId(null);
	};

	return (
		<Popup
			onSelectOption={handleSelectPopup}
			title="Fit Type"
			varient={POPUP_VARIENTS.DROP_DOWN}
			selectedOptions={selectedFitTypes}
			options={fitTypes}
			onCancel={handleCancel}
			onSubmit={() => handleSubmitFitType()}
			handleRemoveSelectedOptions={handleRemoveSelectedOptions}
		/>
	);
};

// GSM Popup Component
export const GSMPopup = ({ setSelectedTargetGSMId, onUpdateTarget, item, id, entityType }) => {
	const { handleInputChange, resetValues, values, setValues } = useFormInput(INITIAL_STATE);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		setValues({ min: item?.targetMinGsm, max: item?.targetMaxGsm });
	}, [id, entityType]);

	const handleSubmitGSM = () => {
		if (validatePopupRequiredFields(values, 'GSM', setErrors)) {
			return;
		}
		const body = {
			id: checkValidId(item),
			entityId: id,
			fabricType: item?.fabricType?.constant,
			categoryId: item?.categoryResponse?.id,
			productGroupId: item?.rangePlanningId?.productGroupId,
			fieldName: UPDATE_ENTITY_TYPE.GSM,
			targetMinGsm: parseInt(values?.min),
			targetMaxGsm: parseInt(values?.max),
			entityType: entityType
		};

		const reset = () => {
			setSelectedTargetGSMId(null);
			resetValues();
		};
		onUpdateTarget(body, reset);
	};

	return (
		<Popup
			title="GSM Popup"
			varient={POPUP_VARIENTS.INPUT}
			onInputChange={handleInputChange}
			inputs={[
				{ label: 'Min', error: errors.min, type: 'text', value: values.min, name: 'min' },
				{ label: 'Max', error: errors.max, type: 'text', value: values.max, name: 'max' }
			]}
			onCancel={() => setSelectedTargetGSMId(null)}
			onSubmit={handleSubmitGSM}
		/>
	);
};

// Price Range Popup Component
export const PriceRange = ({
	onUpdateTarget,
	item,
	id,
	entityType,
	setSelectedTargetPriceRangeId,
	brandName
}) => {
	const { handleInputChange, resetValues, values, setValues } = useFormInput(INITIAL_STATE);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		setValues({
			min: item?.targetMinPrice,
			max: item?.targetMaxPrice
		});
	}, [id, entityType]);

	const handleSubmitPriceRange = () => {
		if (validatePopupRequiredFields(values, 'Price', setErrors)) {
			return;
		}
		const body = {
			id: checkValidId(item),
			entityId: id,
			categoryId: item?.categoryResponse?.id,
			productGroupId: item?.rangePlanningId?.productGroupId,
			fieldName: UPDATE_ENTITY_TYPE.PRICE,
			targetMinPrice: parseFloat(values?.min),
			targetMaxPrice: parseFloat(values?.max),
			entityType: entityType,
			fabricType: item?.fabricType?.constant
		};
		const reset = () => {
			setSelectedTargetPriceRangeId(null);
			resetValues();
		};
		onUpdateTarget(body, reset);
	};

	return (
		<Popup
			onInputChange={handleInputChange}
			varient={POPUP_VARIENTS.INPUT}
			title="Quantity Popup"
			inputs={[
				{ label: 'Min ($)', error: errors.min, type: 'number', value: values.min, name: 'min' },
				{ label: 'Max ($)', error: errors.max, type: 'number', value: values.max, name: 'max' }
			]}
			onCancel={() => setSelectedTargetPriceRangeId(null)}
			onSubmit={handleSubmitPriceRange}
			errors={errors}
			isShowBrandInfo={true}
			brandPriceDetails={{
				brandMaxPrice: item?.brandMaxPrice,
				brandMinPrice: item?.brandMinPrice,
				brandName
			}}
		/>
	);
};
